import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { Text } from "../../components/ui/Text";
import { Table, TableColumn } from "../../components/ui/Table";
import { api } from "../../services/api";
import { FaCheck, FaChevronDown, FaChevronUp } from "react-icons/fa";

interface ContributorPayout {
  _id: string;
  contributorId?: {
    _id: string;
    name: string;
    email?: string;
  };
  periodStart: string;
  periodEnd: string;
  totalAmount: number;
  currency: string;
  status: "pending" | "paid" | "cancelled";
  paidAt?: string;
  paidBy?: {
    _id: string;
    name: string;
  };
  metadata?: {
    countryCode?: string;
    storeOwnerId?: string;
  };
}

interface Location {
  _id: string;
  country: string;
  countryCode: string;
  currency: string;
}

export const ContributorPayouts: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [payouts, setPayouts] = useState<ContributorPayout[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("USD");
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [periodStart, setPeriodStart] = useState<string>("");
  const [periodEnd, setPeriodEnd] = useState<string>("");
  const [expandedPayout, setExpandedPayout] = useState<string | null>(null);
  const [payoutBreakdown, setPayoutBreakdown] = useState<Record<string, any>>({});
  const [loadingBreakdown, setLoadingBreakdown] = useState<Record<string, boolean>>({});
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({});
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    loadLocations();
    loadPayouts();
  }, []);

  useEffect(() => {
    if (selectedCurrency && payouts.length > 0) {
      loadExchangeRates();
    }
  }, [selectedCurrency, payouts]);

  const loadExchangeRates = async () => {
    setConverting(true);
    try {
      const uniqueCurrencies = Array.from(new Set(payouts.map((p) => p.currency)));
      const rates: Record<string, number> = {};

      // Get exchange rates for all unique currencies to selected currency
      for (const currency of uniqueCurrencies) {
        if (currency === selectedCurrency) {
          rates[currency] = 1;
        } else {
          try {
            // Try to get exchange rate from backend
            const response = await api.get(
              `/currencies/admin/fetch-rate?from=${currency}&to=${selectedCurrency}&source=exchangerate-api`
            );
            if (response.data.rate) {
              rates[currency] = response.data.rate;
            } else {
              rates[currency] = 1; // Fallback to 1 if rate not available
            }
          } catch (err) {
            console.error(`Failed to fetch rate for ${currency} to ${selectedCurrency}:`, err);
            rates[currency] = 1; // Fallback to 1 if rate fetch fails
          }
        }
      }

      setExchangeRates(rates);
    } catch (err) {
      console.error("Failed to load exchange rates:", err);
    } finally {
      setConverting(false);
    }
  };

  const convertAmount = (amount: number, fromCurrency: string): number => {
    if (fromCurrency === selectedCurrency) {
      return amount;
    }
    const rate = exchangeRates[fromCurrency] || 1;
    return amount * rate;
  };

  const loadLocations = async () => {
    try {
      const response = await api.get("/locations/admin");
      setLocations(response.data.locations || []);
    } catch (err: any) {
      console.error("Failed to load locations:", err);
      setLocations([]);
    }
  };

  const loadPayouts = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (selectedCountry) params.countryCode = selectedCountry;
      if (periodStart) params.periodStart = periodStart;
      if (periodEnd) params.periodEnd = periodEnd;

      const response = await api.get("/profit-rules/payouts", { params });
      setPayouts(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.contributorPayouts.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleCalculatePayouts = async () => {
    if (!periodStart || !periodEnd) {
      setError(t("admin.contributorPayouts.errors.selectPeriod"));
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await api.post("/profit-rules/payouts/calculate", {
        periodStart,
        periodEnd,
        countryCode: selectedCountry || undefined,
      });
      setPayouts(response.data);
      setSuccess(t("admin.contributorPayouts.success.calculated"));
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.contributorPayouts.errors.calculateFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAsPaid = async (payoutId: string) => {
    if (!window.confirm(t("admin.contributorPayouts.confirm.markPaid"))) return;

    setSaving(true);
    setError(null);
    try {
      await api.put(`/profit-rules/payouts/${payoutId}/mark-paid`);
      await loadPayouts();
      setSuccess(t("admin.contributorPayouts.success.markedPaid"));
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.contributorPayouts.errors.markPaidFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleExpandPayout = async (payoutId: string) => {
    if (expandedPayout === payoutId) {
      setExpandedPayout(null);
      return;
    }

    setExpandedPayout(payoutId);
    
    // Load breakdown for this payout
    if (!payoutBreakdown[payoutId]) {
      setLoadingBreakdown((prev) => ({ ...prev, [payoutId]: true }));
      try {
        const response = await api.get(`/profit-rules/payouts/${payoutId}/breakdown`);
        setPayoutBreakdown((prev) => ({ ...prev, [payoutId]: response.data }));
      } catch (err: any) {
        console.error("Failed to load payout breakdown:", err);
        setError(err.response?.data?.error || t("admin.contributorPayouts.details.breakdownLoadFailed"));
      } finally {
        setLoadingBreakdown((prev) => ({ ...prev, [payoutId]: false }));
      }
    }
  };

  // Calculate net payouts (differences when contributors pay each other)
  const calculateNetPayouts = () => {
    const netPayouts: Record<string, { amount: number; currency: string; name: string }> = {};

    for (const payout of payouts) {
      const key = payout.contributorId?._id || `store-owner-${payout.metadata?.storeOwnerId || "unknown"}`;
      const name = payout.contributorId?.name || t("admin.contributorPayouts.storeOwner");
      
      if (!netPayouts[key]) {
        netPayouts[key] = { amount: 0, currency: selectedCurrency, name };
      }

      // Convert to selected currency
      const convertedAmount = convertAmount(payout.totalAmount, payout.currency);
      netPayouts[key].amount += convertedAmount;
    }

    return netPayouts;
  };

  const netPayouts = calculateNetPayouts();

  // Get unique currencies
  const currencies = Array.from(new Set(payouts.map((p) => p.currency)));

  const columns: TableColumn<ContributorPayout>[] = [
    {
      key: "contributor",
      header: t("admin.contributorPayouts.columns.contributor"),
      render: (payout) => payout.contributorId?.name || t("admin.contributorPayouts.storeOwner"),
    },
    {
      key: "amount",
      header: t("admin.contributorPayouts.columns.amount"),
      render: (payout) => {
        const convertedAmount = convertAmount(payout.totalAmount, payout.currency);
        const originalAmount = payout.totalAmount;
        const originalCurrency = payout.currency;
        
        if (originalCurrency === selectedCurrency) {
          return `${selectedCurrency} ${convertedAmount.toFixed(2)}`;
        }
        
        return (
          <div>
            <div className="font-medium">{selectedCurrency} {convertedAmount.toFixed(2)}</div>
            <div className="text-xs text-gray-500">
              {originalCurrency} {originalAmount.toFixed(2)}
            </div>
          </div>
        );
      },
    },
    {
      key: "status",
      header: t("admin.contributorPayouts.columns.status"),
      render: (payout) => (
        <span
          className={`px-2 py-1 rounded text-xs ${
            payout.status === "paid"
              ? "bg-green-100 text-green-800"
              : payout.status === "cancelled"
              ? "bg-red-100 text-red-800"
              : "bg-yellow-100 text-yellow-800"
          }`}
        >
          {t(`admin.payouts.status.${payout.status}`)}
        </span>
      ),
    },
    {
      key: "period",
      header: t("admin.contributorPayouts.columns.period"),
      render: (payout) => {
        const start = new Date(payout.periodStart).toLocaleDateString();
        const end = new Date(payout.periodEnd).toLocaleDateString();
        return `${start} - ${end}`;
      },
    },
    {
      key: "actions",
      header: t("admin.contributorPayouts.columns.actions"),
      render: (payout) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleExpandPayout(payout._id)}
          >
            {expandedPayout === payout._id ? <FaChevronUp /> : <FaChevronDown />}
          </Button>
          {payout.status === "pending" && (
            <Button
              size="sm"
              onClick={() => handleMarkAsPaid(payout._id)}
              disabled={saving}
            >
              <FaCheck className="mr-1" />
              {t("admin.contributorPayouts.markPaid")}
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <Container>
        <Stack spacing="lg">
          <Spinner />
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="lg">
        <Heading size="xl">{t("admin.contributorPayouts.title")}</Heading>
        <Text className="text-gray-600 dark:text-gray-400">
          {t("admin.contributorPayouts.description")}
        </Text>

        {error && <Alert variant="error">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {/* Filters and Controls */}
        <Card padding="lg">
          <Stack spacing="md">
            <Heading size="md">{t("admin.contributorPayouts.calculate.title")}</Heading>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Input
                label={t("admin.contributorPayouts.periodStart")}
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
              <Input
                label={t("admin.contributorPayouts.periodEnd")}
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
              <Select
                label={t("admin.contributorPayouts.countryOptional")}
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                options={[
                  { value: "", label: t("admin.contributorPayouts.allCountries") },
                  ...(Array.isArray(locations) ? locations.map((loc) => ({
                    value: loc.countryCode,
                    label: loc.country,
                  })) : []),
                ]}
              />
              <div className="flex items-end">
                <Button onClick={handleCalculatePayouts} disabled={saving || !periodStart || !periodEnd}>
                  {t("admin.contributorPayouts.calculate.button")}
                </Button>
              </div>
            </div>
          </Stack>
        </Card>

        {/* Currency Selector */}
        {currencies.length > 0 && (
          <Card padding="md">
            <div className="flex items-center gap-4">
              <Text className="font-medium">{t("admin.contributorPayouts.viewInCurrency")}:</Text>
              <Select
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                options={
                  currencies.length > 0
                    ? currencies.map((curr) => ({ value: curr, label: curr }))
                    : [{ value: "USD", label: "USD" }]
                }
              />
              {converting && <Spinner size="sm" />}
            </div>
          </Card>
        )}

        {/* Net Payouts Summary */}
        {Object.keys(netPayouts).length > 0 && (
          <Card padding="lg">
            <Stack spacing="md">
              <Heading size="md">{t("admin.contributorPayouts.netPayouts")}</Heading>
              <div className="space-y-2">
                {Object.entries(netPayouts).map(([key, net]) => (
                  <div key={key} className="flex justify-between p-3 border border-gray-200 dark:border-gray-700 rounded">
                    <Text className="font-medium">{net.name}</Text>
                    <Text className="font-medium">
                      {net.currency} {net.amount.toFixed(2)}
                    </Text>
                  </div>
                ))}
              </div>
            </Stack>
          </Card>
        )}

        {/* Payouts Table */}
        <Card padding="lg">
          <Stack spacing="md">
            <Heading size="md">{t("admin.contributorPayouts.details.title")}</Heading>
            {payouts.length === 0 ? (
              <Text className="text-gray-500">{t("admin.contributorPayouts.noPayouts")}</Text>
            ) : (
              <>
                <Table columns={columns} data={payouts} />
                {payouts.map((payout) => (
                  expandedPayout === payout._id && (
                    <Card key={payout._id} padding="md" className="mt-4">
                      <Stack spacing="md">
                        <Heading size="sm">{t("admin.contributorPayouts.details.title")}</Heading>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <Text className="font-medium">{t("admin.contributorPayouts.details.contributor")}:</Text>
                            <Text>{payout.contributorId?.name || t("admin.contributorPayouts.storeOwner")}</Text>
                          </div>
                          <div>
                            <Text className="font-medium">{t("admin.contributorPayouts.details.amount")}:</Text>
                            <Text>
                              {payout.currency} {payout.totalAmount.toFixed(2)}
                            </Text>
                          </div>
                          <div>
                            <Text className="font-medium">{t("admin.contributorPayouts.details.status")}:</Text>
                            <Text>{t(`admin.payouts.status.${payout.status}`)}</Text>
                          </div>
                          {payout.paidAt && (
                            <div>
                              <Text className="font-medium">{t("admin.contributorPayouts.details.paidAt")}:</Text>
                              <Text>{new Date(payout.paidAt).toLocaleString()}</Text>
                            </div>
                          )}
                          {payout.paidBy && (
                            <div>
                              <Text className="font-medium">{t("admin.contributorPayouts.details.paidBy")}:</Text>
                              <Text>{payout.paidBy.name}</Text>
                            </div>
                          )}
                        </div>

                        {/* Breakdown */}
                        <div className="mt-4">
                          <Heading size="sm" className="mb-3">{t("admin.contributorPayouts.details.breakdownTitle")}</Heading>
                          {loadingBreakdown[payout._id] ? (
                            <div className="flex justify-center py-4">
                              <Spinner />
                            </div>
                          ) : payoutBreakdown[payout._id]?.profitDetails?.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      {t("admin.contributorPayouts.details.order")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      {t("admin.contributorPayouts.details.product")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      {t("admin.contributorPayouts.details.itemPrice")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      {t("admin.contributorPayouts.details.materialCost")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      {t("admin.contributorPayouts.details.netPrice")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      {t("admin.contributorPayouts.details.profitPercentage")}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      {t("admin.contributorPayouts.details.profitAmount")}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {payoutBreakdown[payout._id].profitDetails.map((item: any, index: number) => (
                                    <tr key={index}>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                                        {item.order?.orderNumber || `#${item.profitDetail.orderId?.toString().slice(-6)}`}
                                        <br />
                                        <span className="text-gray-500 text-xs">
                                          {item.order?.createdAt ? new Date(item.order.createdAt).toLocaleDateString() : ""}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-sm">
                                        {item.product?.title || item.orderItem?.product?.title || "N/A"}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                                        {item.profitDetail.currency} {item.orderItem?.price?.toFixed(2) || "0.00"}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                                        {item.profitDetail.currency} {item.profitDetail.materialCost.toFixed(2)}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                                        {item.profitDetail.currency} {item.profitDetail.netPrice.toFixed(2)}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                                        {item.profitDetail.profitPercentage.toFixed(2)}%
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                                        {item.profitDetail.currency} {item.profitDetail.profitAmount.toFixed(2)}
                                      </td>
                                    </tr>
                                  ))}
                                  <tr className="bg-gray-50 font-semibold">
                                    <td colSpan={6} className="px-4 py-3 text-right text-sm">
                                      {t("admin.contributorPayouts.details.total")}:
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                                      {payout.currency} {payout.totalAmount.toFixed(2)}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <Text className="text-gray-500">{t("admin.contributorPayouts.details.noProfitDetails")}</Text>
                          )}
                        </div>
                      </Stack>
                    </Card>
                  )
                ))}
              </>
            )}
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
};
